import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Button, IconButton, InlineLoading, Tag } from "@carbon/react";
import { Add, TrashCan } from "@carbon/icons-react";
import DateField from "../../../components/carbon/Form/DateField";
import Form from "../../../components/carbon/Form/Form";
import TextField from "../../../components/carbon/Form/TextField";
import { useNotification } from "../../../context";
import {
  useOcHolidays,
  useReplaceOcHolidays,
} from "../../../hooks/useOcHolidays";
import {
  PurchaseOrderHoliday,
  PurchaseOrderHolidayInput,
} from "../../../types";

/** One company-specific holiday row. `date` is an ISO `yyyy-MM-dd` string, `""` while empty. */
interface HolidayRow {
  date: string;
  label: string;
}

interface HolidayFormFields {
  holidays: HolidayRow[];
}

const MAX_LABEL = 255;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const emptyHolidayRow: HolidayRow = { date: "", label: "" };

const byDate = (a: PurchaseOrderHoliday, b: PurchaseOrderHoliday) =>
  a.date.localeCompare(b.date);

const toFormFields = (holidays: PurchaseOrderHoliday[]): HolidayFormFields => ({
  holidays: holidays
    .filter((holiday) => !holiday.national)
    .sort(byDate)
    .map((holiday) => ({ date: holiday.date, label: holiday.label ?? "" })),
});

/** The REPLACE body: the company-specific set only (national rows are never sent). */
const toRequest = (rows: HolidayRow[]): PurchaseOrderHolidayInput[] =>
  rows.map((row) => ({ date: row.date, label: row.label.trim() }));

const buildSchema = (t: (key: string) => string) =>
  Yup.object({
    holidays: Yup.array()
      .of(
        Yup.object({
          date: Yup.string()
            .required(t("purchaseOrders.settings.holidays.validation.date"))
            .matches(
              ISO_DATE,
              t("purchaseOrders.settings.holidays.validation.date")
            ),
          label: Yup.string()
            .default("")
            .max(
              MAX_LABEL,
              t("purchaseOrders.settings.holidays.validation.label")
            ),
        })
      )
      .default([])
      .test(
        "unique-dates",
        t("purchaseOrders.settings.holidays.validation.duplicate"),
        (rows?: { date?: string }[]) => {
          const dates = (rows ?? [])
            .map((row) => row?.date)
            .filter((date) => Boolean(date));
          return new Set(dates).size === dates.length;
        }
      ),
  });

const errorMessage = (error: unknown): string | undefined =>
  (error as { message?: string; root?: { message?: string } } | undefined)?.root
    ?.message ?? (error as { message?: string } | undefined)?.message;

const statusOf = (error: unknown) =>
  (error as { response?: { status?: number } } | null)?.response?.status;

/** The company's country holidays: shown for context, never editable or removable. */
const NationalHolidays = ({
  holidays,
}: {
  holidays: PurchaseOrderHoliday[];
}) => {
  const { t } = useTranslation();
  if (holidays.length === 0) return null;
  return (
    <div className="oc-settings__national">
      <h3 className="oc-settings__subheading">
        {t("purchaseOrders.settings.holidays.nationalTitle")}
      </h3>
      <ul className="oc-settings__national-list">
        {holidays.map((holiday) => (
          <li key={holiday.date} className="oc-settings__national-item">
            <span className="oc-settings__national-date">{holiday.date}</span>
            {holiday.label ? <span>{holiday.label}</span> : null}
            <Tag type="gray" size="sm">
              {t("purchaseOrders.settings.holidays.national")}
            </Tag>
          </li>
        ))}
      </ul>
    </div>
  );
};

const HolidayCalendarForm = ({
  baseline,
  national,
}: {
  baseline: HolidayFormFields;
  national: PurchaseOrderHoliday[];
}) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const replaceHolidays = useReplaceOcHolidays();
  const [saving, setSaving] = useState(false);
  const schema = useMemo(() => buildSchema(t), [t]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<HolidayFormFields>({
    resolver: yupResolver(schema) as never,
    defaultValues: baseline,
  });

  // A (re)fetched calendar is the new baseline.
  useEffect(() => {
    reset(baseline);
  }, [baseline, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "holidays",
  });

  const onSubmit = async (values: HolidayFormFields) => {
    setSaving(true);
    try {
      await replaceHolidays.mutateAsync(toRequest(values.holidays));
      reset(values);
      openNotification(t("purchaseOrders.settings.holidays.success"));
    } catch (error) {
      openNotification(
        t(
          statusOf(error) === 400
            ? "purchaseOrders.settings.holidays.invalid"
            : "purchaseOrders.settings.holidays.error"
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      noValidate
      className="oc-settings__form"
    >
      <section className="oc-settings__section">
        <div className="oc-settings__vat-header">
          <h2 className="oc-settings__heading">
            {t("purchaseOrders.settings.holidays.title")}
          </h2>
          <Button
            kind="tertiary"
            size="md"
            renderIcon={Add}
            type="button"
            onClick={() => append(emptyHolidayRow)}
          >
            {t("purchaseOrders.settings.holidays.add")}
          </Button>
        </div>
        <p className="oc-settings__help">
          {t("purchaseOrders.settings.holidays.help")}
        </p>

        <NationalHolidays holidays={national} />

        {errorMessage(errors.holidays) ? (
          <p role="alert" className="oc-settings__error">
            {errorMessage(errors.holidays)}
          </p>
        ) : null}
        {fields.length === 0 ? (
          <p className="oc-settings__empty">
            {t("purchaseOrders.settings.holidays.empty")}
          </p>
        ) : (
          <div className="oc-settings__rows">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="oc-settings__row"
                role="group"
                aria-label={t("purchaseOrders.settings.holidays.row", {
                  index: index + 1,
                })}
              >
                <div className="oc-settings__grid oc-settings__grid--holiday">
                  <DateField
                    name={`holidays.${index}.date`}
                    label={t("purchaseOrders.settings.holidays.date")}
                    control={control}
                    placeholder="yyyy-mm-dd"
                  />
                  <TextField
                    name={`holidays.${index}.label`}
                    label={t("purchaseOrders.settings.holidays.label")}
                    control={control}
                    maxLength={MAX_LABEL}
                  />
                </div>
                <IconButton
                  kind="ghost"
                  size="md"
                  label={t("purchaseOrders.settings.holidays.remove", {
                    index: index + 1,
                  })}
                  onClick={() => remove(index)}
                >
                  <TrashCan />
                </IconButton>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="oc-settings__actions">
        <Button type="submit" disabled={saving || !isDirty}>
          {t("purchaseOrders.settings.holidays.save")}
        </Button>
      </div>
    </Form>
  );
};

/**
 * Holiday calendar admin of the OC settings screen. Lists the company's own holidays as editable
 * dated rows and its country's national holidays read-only; Save REPLACES the company-specific
 * set (national rows are never sent) and stays disabled until something differs from what was
 * loaded. A 400 (missing / duplicate date) is an error toast, never a crash. Access is gated by
 * the route (MANAGE_PURCHASE_ORDER_APPROVALS + the OC module), exactly like the rest of the screen.
 */
const HolidayCalendar = () => {
  const { t } = useTranslation();
  const holidaysQuery = useOcHolidays();
  const holidays = holidaysQuery.data;

  const baseline = useMemo(
    () => (holidays ? toFormFields([...holidays]) : undefined),
    [holidays]
  );
  const national = useMemo(
    () => (holidays ?? []).filter((holiday) => holiday.national).sort(byDate),
    [holidays]
  );

  if (holidaysQuery.isLoading) {
    return <InlineLoading description={t("cta.loading")} />;
  }
  if (holidaysQuery.isError || !baseline) {
    return (
      <p role="alert">{t("purchaseOrders.settings.holidays.loadError")}</p>
    );
  }
  return <HolidayCalendarForm baseline={baseline} national={national} />;
};

export default HolidayCalendar;
